import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { CircularProgress, Tooltip, makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import Pagination from '@material-ui/lab/Pagination';
import {
  TEACHERS_HOME_URL,
  TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
} from 'config/urls';
import { get, isNil } from 'lodash';
import {
  trackerInstanceSectionDismiss,
  usePriorityGradingAssignments
} from 'sdk';
import { cutText } from 'utils';

import { ReactComponent as DeleteIcon } from 'pages/Teachers/Home/assets/delete-icon.svg';
import { ReactComponent as ErrorIcon } from 'pages/Teachers/Home/assets/error-triangle-icon.svg';
import { ReactComponent as PendingIcon } from 'pages/Teachers/Home/assets/pending-icon.svg';
import { ReactComponent as UnpublishedIcon } from 'pages/Teachers/Home/assets/unpublished-icon.svg';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import { notifyError, notifySuccess } from 'utils/notifications';
import { parseParams, stringifyParams } from 'utils/urls';
import { reverse } from 'utils/urls';

import UserContext from 'components/UserContext';

import styles from './styles.module.scss';

const tooltipUseStyles = makeStyles({
  customTooltip: {
    backgroundColor: '#333867',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'Nunito'
  }
});

const PriorityGradingList = ({
  initialData,
  isLoading: isParentLoading,
  refetchData
}) => {
  const [items, setItems] = useState(initialData?.results || []);
  const [deletingId, setDeletingId] = useState(null);
  const listRef = useRef(null);

  const { user } = useContext(UserContext);
  const tooltipStyles = tooltipUseStyles();

  const [pagination, setPagination] = useState({
    numPages: initialData
      ? Math.ceil(initialData.count / initialData.limit)
      : 1,
    currentPage: 1,
    limit: 3
  });

  const history = useHistory();
  const searchParams = parseParams(history.location.search);
  const urlPage = get(searchParams, 'currentPage');

  const {
    data: priorityGradingAssignments,
    isLoading: isLoadingLocal,
    refetch: priorityGradingAssignmentsRefetch
  } = usePriorityGradingAssignments({
    params: {
      offset: (pagination.currentPage - 1) * pagination.limit,
      limit: pagination.limit
    }
  });

  const isLoading = isParentLoading || isLoadingLocal;

  useEffect(() => {
    if (!isNil(urlPage)) {
      setPagination((prev) => ({
        ...prev,
        currentPage: parseInt(urlPage)
      }));

      history.replace({
        search: ''
      });
    }
  }, [urlPage, history]);

  useEffect(() => {
    if (isNil(priorityGradingAssignments)) return;

    const { count, limit, results } = priorityGradingAssignments;

    setItems(results);
    setPagination((prev) => ({
      ...prev,
      numPages: Math.ceil(count / limit)
    }));
  }, [priorityGradingAssignments]);

  const handleItemClick = ({ item }) => {
    trackMixpanelEvent(user.id, 'assignment_opened', {
      feature: 'priority grading',
      view: 'home page',
      normalizedURL:
        process.env.REACT_APP_BASE_FRONTEND_URL + TEACHERS_HOME_URL,
      assignment_id: item.id,
      assignment_name: item.name,
      class_name: item.section_name
    });

    history.push({
      pathname: reverse(TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL, {
        trackerInstanceSectionId: item.id
      }),
      search: stringifyParams({
        currentPage: pagination.currentPage
      })
    });
  };

  const handleItemRemove = async ({ item }) => {
    setDeletingId(item.id);

    try {
      const { success } = await trackerInstanceSectionDismiss({
        trackerInstanceSectionId: item.id
      });

      if (success) {
        notifySuccess('Assignment hidden successfully!');

        const isLastItemOnPage = items.length === 1;
        const isNotFirstPage = pagination.currentPage > 1;

        if (isLastItemOnPage && isNotFirstPage) {
          setPagination((prev) => ({
            ...prev,
            currentPage: prev.currentPage - 1
          }));
        } else {
          priorityGradingAssignmentsRefetch();
          if (refetchData) refetchData();
        }

        trackMixpanelEvent(user.id, 'assignment_dismissed', {
          feature: 'priority grading',
          view: 'home page',
          normalizedURL:
            process.env.REACT_APP_BASE_FRONTEND_URL + TEACHERS_HOME_URL,
          assignment_id: item.id,
          assignment_name: item.name,
          class_name: item.section_name
        });
      } else {
        notifyError('Failed to hide assignment. Please try again.');
      }
    } catch (error) {
      notifyError('An unexpected error occurred.');
    } finally {
      setTimeout(() => {
        setDeletingId(null);
      }, 1000);
    }
  };

  const changePageAndRefetchListData = (page) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      currentPage: page
    }));
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.scrollContainer} ref={listRef}>
        {isLoading ? (
          <div className={styles.loadingState}>
            <Skeleton variant="rect" width="100%" height={104} />
            <Skeleton variant="rect" width="100%" height={104} />
            <Skeleton variant="rect" width="100%" height={104} />
          </div>
        ) : (
          items.map((item) => (
            <div
              key={item.id}
              className={styles.card}
              onClick={() => handleItemClick({ item })}
            >
              <div className={styles.cardHeader}>
                <h3 className={styles.cardTitle}>{item.section_name}</h3>
                <Tooltip
                  title="Dismiss this notification to hide it permanently."
                  classes={{ tooltip: tooltipStyles.customTooltip }}
                  placement="right"
                >
                  <button
                    className={styles.closeButton}
                    disabled={deletingId === item.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleItemRemove({ item });
                    }}
                    aria-label="Remove item"
                  >
                    {deletingId === item.id ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <DeleteIcon />
                    )}
                  </button>
                </Tooltip>
              </div>

              <Tooltip
                title={item.name}
                classes={{ tooltip: tooltipStyles.customTooltip }}
                disableHoverListener={item.name.length < 70}
                placement="right"
              >
                <p className={styles.cardSubtitle}>
                  {cutText({ text: item.name, char: 70 })}
                </p>
              </Tooltip>

              <div className={styles.cardBadges}>
                <div className={`${styles.badge} ${styles.badgeLate}`}>
                  <span className={styles.badgeIconPlaceholder}>
                    <PendingIcon />
                  </span>
                  <span>Late ({item.late_count})</span>
                </div>
                <div className={`${styles.badge} ${styles.badgeUnpublished}`}>
                  <span className={styles.badgeIconPlaceholder}>
                    <UnpublishedIcon />
                  </span>
                  <span>Unpublished ({item.unpublished_count})</span>
                </div>
                <div className={`${styles.badge} ${styles.badgeReupload}`}>
                  <span className={styles.badgeIconPlaceholder}>
                    <ErrorIcon />
                  </span>
                  <span>Reupload Needed ({item.needs_review_count})</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      <div className={styles.paginationContainer}>
        {pagination.numPages > 1 && (
          <div className={styles.pagination}>
            <Pagination
              count={pagination.numPages}
              page={pagination.currentPage}
              color="secondary"
              onChange={(event, page) => changePageAndRefetchListData(page)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PriorityGradingList;
